import { Fragment, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, fehlertext } from '../api'
import { useAktion, useLaden } from '../hooks'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import { MONATE_KURZ, datum, differenz, menge, preis, stueck, tonnen, zahlLesen, zahlText } from '../format'
import { INVENTURSTATUS } from '../texte'
import type {
  Inventur,
  InventurBlatt,
  InventurZeile,
  Jahreszeile,
  ZaehlzettelAntwort,
} from '../typen'

/**
 * Der Inventur-Monatslauf - Zählliste, Inventurlisten_Detail, Bestandsliste mit Umrechnung und
 * Inventur-Auswertung in einer Ansicht.
 *
 * Je Material ein Zählblock in den Zähleinheiten der Vorlage (Palette, Rolle, angefangene Rolle
 * in cm, Sperrlager), daneben das Soll aus SelectLine und die Differenz in Stück, Tonnen und Euro.
 * Ein freier Zählzettel lässt sich von der KI den Zähleinheiten zuordnen; gerechnet wird immer
 * vom Backend mit den Faktoren aus den Stammdaten. Der Abschluss macht aus der Zählung einen
 * Import, der wie jeder andere freigegeben wird - erst dann steht der Bestand in der Kladde.
 */


type Eingaben = Record<string, string[]>

/** Ab dieser Anzahl je Zähleinheit fragt die Seite nach - ein Tippfehler mit einer Null zu viel fällt sonst erst in der Differenz auf. */
const ANZAHL_RUECKFRAGE = 1_000_000

/**
 * Die Zählfelder eines Blocks prüfen, bevor gerechnet oder gespeichert wird. Text, der keine Zahl ist,
 * und negative Anzahlen werden genannt statt stumm verworfen - in der Sandbox (17.09.2026) wurde „abc"
 * als „gespeichert" gemeldet und die Summe zeigte −20.000 an.
 */
function zaehlfehler(zeile: InventurZeile, werte: string[]): string | null {
  const fehler: string[] = []
  zeile.zaehleinheiten.forEach((einheit, i) => {
    const text = (werte[i] ?? '').trim()
    if (text === '') return
    const wert = zahlLesen(text)
    if (wert === null) fehler.push(`„${text}" ist keine Anzahl (${einheit.bezeichnung}).`)
    else if (wert < 0) fehler.push(`„${text}" ist negativ - eine Anzahl kann nicht unter 0 liegen (${einheit.bezeichnung}).`)
  })
  return fehler.length > 0 ? fehler.join(' ') : null
}

/** Gespeicherte Zählzeilen zurück in die Eingabefelder - Duplikate (zwei angefangene Rollen) der Reihe nach. */
function eingabenAusZeile(zeile: InventurZeile): string[] {
  const rest = [...zeile.positionen]
  return zeile.zaehleinheiten.map((einheit) => {
    const index = rest.findIndex((p) => p.bezeichnung === einheit.bezeichnung)
    if (index < 0) return ''
    const [treffer] = rest.splice(index, 1)
    return zahlText(treffer.anzahl)
  })
}

export default function InventurSeite() {
  const navigate = useNavigate()
  const [inventuren, setInventuren] = useState<Inventur[]>([])
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  const [neuStichtag, setNeuStichtag] = useState('')
  const [neuBeleg, setNeuBeleg] = useState('')
  const [gruppe, setGruppe] = useState('')
  const [blatt, setBlatt] = useState<InventurBlatt | null>(null)
  const [eingaben, setEingaben] = useState<Eingaben>({})
  const [vermerke, setVermerke] = useState<Record<string, string>>({})
  const [ansicht, setAnsicht] = useState<'zaehlung' | 'jahr'>('zaehlung')
  const [jahr, setJahr] = useState(new Date().getFullYear())
  const [zettel, setZettel] = useState('')
  const [zettelAntwort, setZettelAntwort] = useState<ZaehlzettelAntwort | null>(null)
  const { fehler, erfolg, arbeitet, setFehler, ausfuehren } = useAktion()
  // Gezählte Materialien sind eingeklappt, damit die Seite bei 55 Materialien überschaubar bleibt;
  // ein Klick auf den Kopf oder die Sprungleiste klappt sie wieder auf.
  const [aufgeklappt, setAufgeklappt] = useState<Set<string>>(new Set())
  // Liste und Blatt laden von Hand (die Liste ohne Überhol-Schutz, das Blatt mit Nebenwirkungen auf
  // Eingaben und Vermerke) - deshalb eigene Lade-Schalter, damit die Seite nicht leer bleibt.
  const [laedtListe, setLaedtListe] = useState(true)
  const [laedtBlatt, setLaedtBlatt] = useState(false)
  /** Der Block, auf den die Sprungleiste gerade gesprungen ist - 1,5 s mit Rahmen hervorgehoben. */
  const [hervorgehoben, setHervorgehoben] = useState<string | null>(null)
  /** Fehler je Zählblock (Artikelnummer): steht am Block, nicht nur oben - bei 55 Blöcken ist oben weit weg. */
  const [blockFehler, setBlockFehler] = useState<Record<string, string>>({})

  const listeLaden = useCallback(() => {
    setLaedtListe(true)
    api
      .inventuren()
      .then((werte) => {
        setInventuren(werte)
        if (werte.length > 0) setGewaehlt((alt) => alt ?? werte[0].id)
      })
      .catch((f) => setFehler(fehlertext(f)))
      .finally(() => setLaedtListe(false))
    api
      .stichtagVorschlag()
      .then((v) => setNeuStichtag((alt) => alt || v.stichtag))
      .catch(() => undefined)
  }, [setFehler])

  useEffect(listeLaden, [listeLaden])

  const blattLaden = useCallback(() => {
    if (gewaehlt === null) {
      setBlatt(null)
      return
    }
    setFehler(null)
    setLaedtBlatt(true)
    api
      .inventur(gewaehlt, gruppe || undefined)
      .then((wert) => {
        setBlatt(wert)
        if (!gruppe && wert.gruppen.length > 0) setGruppe(wert.gruppen[0])
        const neu: Eingaben = {}
        const neueVermerke: Record<string, string> = {}
        for (const zeile of wert.zeilen) {
          neu[zeile.artikelnummer] = eingabenAusZeile(zeile)
          neueVermerke[zeile.artikelnummer] = zeile.vermerk ?? ''
        }
        setEingaben(neu)
        setVermerke(neueVermerke)
      })
      .catch((f) => setFehler(fehlertext(f)))
      .finally(() => setLaedtBlatt(false))
  }, [gewaehlt, gruppe, setFehler])

  useEffect(blattLaden, [blattLaden])

  const { daten: jahresDaten, fehler: jahrFehler } = useLaden<Jahreszeile[]>(
    () => api.jahresuebersicht(jahr, gruppe || undefined),
    [jahr, gruppe],
    { wenn: ansicht === 'jahr' },
  )
  const jahresZeilen = jahresDaten ?? []

  function anlegen() {
    if (!neuStichtag) return
    void ausfuehren(async () => {
      const neu = await api.inventurAnlegen(neuStichtag, neuBeleg || null)
      setGewaehlt(neu.id)
      listeLaden()
    })
  }

  function blockFehlerSetzen(nummer: string, text: string | null) {
    setBlockFehler((alt) => {
      const neu = { ...alt }
      if (text) neu[nummer] = text
      else delete neu[nummer]
      return neu
    })
  }

  function speichern(zeile: InventurZeile) {
    const nummer = zeile.artikelnummer
    const werte = eingaben[nummer] ?? []
    const eingabefehler = zaehlfehler(zeile, werte)
    if (eingabefehler) {
      blockFehlerSetzen(nummer, eingabefehler)
      setFehler(`${nummer}: ${eingabefehler}`)
      return
    }
    const positionen = zeile.zaehleinheiten
      .map((einheit, i) => ({ bezeichnung: einheit.bezeichnung, anzahl: zahlLesen(werte[i] ?? '') }))
      .filter((p): p is { bezeichnung: string; anzahl: number } => p.anzahl !== null)
    // Eine Million Paletten gibt es nicht - aber eine Null zu viel schnell. Rückfrage statt Sperre.
    const riesig = positionen.find((p) => p.anzahl > ANZAHL_RUECKFRAGE)
    if (riesig && !window.confirm(`${stueck(riesig.anzahl)} × ${riesig.bezeichnung} - wirklich?`)) return
    blockFehlerSetzen(nummer, null)
    void ausfuehren(async () => {
      if (gewaehlt === null) return
      try {
        await api.zaehlungSpeichern(gewaehlt, nummer, positionen, vermerke[nummer] || null)
      } catch (f) {
        // Das Backend lehnt Unplausibles mit 422 ab („Unplausibel: …"). Der Text gehört an den Block,
        // wo die Eingabe steht - und oben, damit er nach dem Klick sofort auffällt.
        blockFehlerSetzen(nummer, fehlertext(f))
        throw f
      }
      blattLaden()
      listeLaden()
    }, `${nummer} gespeichert.`)
  }

  function zettelZuordnen() {
    if (gewaehlt === null || !zettel.trim()) return
    setZettelAntwort(null)
    void ausfuehren(async () => {
      setZettelAntwort(await api.zaehlzettel(gewaehlt, gruppe || null, zettel))
    })
  }

  function vorschlagUebernehmen(nummer: string, positionen: { bezeichnung: string; anzahl: number }[]) {
    const zeile = blatt?.zeilen.find((z) => z.artikelnummer === nummer)
    if (!zeile || gewaehlt === null) return
    void ausfuehren(async () => {
      await api.zaehlungSpeichern(
        gewaehlt,
        nummer,
        positionen.map((p) => ({ bezeichnung: p.bezeichnung, anzahl: p.anzahl })),
        vermerke[nummer] || null,
      )
      setZettelAntwort((alt) =>
        alt ? { ...alt, zaehlungen: alt.zaehlungen.filter((z) => z.artikelnummer !== nummer) } : alt,
      )
      blattLaden()
      listeLaden()
    }, `${nummer} aus dem Zählzettel übernommen.`)
  }

  function abschliessen() {
    if (gewaehlt === null) return
    void ausfuehren(async () => {
      const neu = await api.inventurAbschliessen(gewaehlt)
      listeLaden()
      blattLaden()
      if (neu.import_lauf_id) navigate(`/importe/${neu.import_lauf_id}`)
    })
  }

  const inventur = blatt?.inventur ?? null
  const bearbeitbar = inventur !== null && inventur.status === 'offen'

  function istEingeklappt(zeile: InventurZeile): boolean {
    return zeile.gezaehlt !== null && !aufgeklappt.has(zeile.artikelnummer)
  }

  function aufklappen(nummer: string, anzeigen = true) {
    setAufgeklappt((alt) => {
      const neu = new Set(alt)
      if (neu.has(nummer)) neu.delete(nummer)
      else neu.add(nummer)
      return neu
    })
    if (anzeigen) {
      window.setTimeout(() => document.getElementById(`zaehlung-${nummer}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 0)
    }
  }

  /** Zum Block springen, ihn kurz mit Rahmen hervorheben und den Cursor ins erste Zählfeld setzen. */
  function springen(zeile: InventurZeile) {
    const nummer = zeile.artikelnummer
    if (istEingeklappt(zeile)) setAufgeklappt((alt) => new Set(alt).add(nummer))
    setHervorgehoben(nummer)
    window.setTimeout(() => {
      const block = document.getElementById(`zaehlung-${nummer}`)
      block?.scrollIntoView({ block: 'start', behavior: 'smooth' })
      block?.querySelector<HTMLInputElement>('input[type="text"]')?.focus({ preventScroll: true })
    }, 0)
    window.setTimeout(() => setHervorgehoben((alt) => (alt === nummer ? null : alt)), 1500)
  }

  const laedt = laedtListe || laedtBlatt
  const gezaehltInGruppe = blatt ? blatt.zeilen.filter((z) => z.gezaehlt !== null).length : 0
  /** Die Jahresübersicht zählt nur Abgeschlossenes - die offene Inventur des Jahres nennt der Leertext. */
  const unfertigeImJahr = inventuren.find((i) => i.status !== 'abgeschlossen' && i.stichtag.startsWith(String(jahr)))
  const monateMitDaten = Array.from(
    new Set(jahresZeilen.flatMap((z) => Object.keys(z.monate).map(Number))),
  ).sort((a, b) => a - b)

  return (
    <>
      <div className="leiste">
        <label>
          Inventur{' '}
          <select value={gewaehlt ?? ''} onChange={(e) => setGewaehlt(e.target.value ? Number(e.target.value) : null)}>
            {inventuren.length === 0 && <option value="">noch keine</option>}
            {inventuren.map((i) => (
              <option key={i.id} value={i.id}>
                {datum(i.stichtag)}
                {i.beleg ? ` · ${i.beleg}` : ''} · {INVENTURSTATUS[i.status].text}
              </option>
            ))}
          </select>
        </label>
        <label>
          Gruppe{' '}
          <select value={gruppe} onChange={(e) => setGruppe(e.target.value)}>
            {(blatt?.gruppen ?? []).map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
        <span className="hinweis">Neue Inventur zum Stichtag</span>
        <input type="date" value={neuStichtag} onChange={(e) => setNeuStichtag(e.target.value)} />
        <input
          type="text"
          style={{ width: 120 }}
          placeholder="Beleg INV…"
          value={neuBeleg}
          onChange={(e) => setNeuBeleg(e.target.value)}
        />
        <button disabled={arbeitet || !neuStichtag} onClick={anlegen}>
          Anlegen
        </button>
      </div>

      <Meldungen fehler={jahrFehler ?? fehler} erfolg={erfolg} />

      {inventur && (
        <div className="leiste">
          <h2>Inventur {datum(inventur.stichtag)}</h2>
          <span className={`status ${INVENTURSTATUS[inventur.status].klasse}`}>{INVENTURSTATUS[inventur.status].text}</span>
          {blatt && gruppe ? (
            <span>
              {gruppe}: {gezaehltInGruppe} von {blatt.zeilen.length} gezählt
              <span className="hinweis">
                {' '}
                · alle Gruppen: {inventur.anzahl_gezaehlt} von {inventur.anzahl_materialien}
              </span>
            </span>
          ) : (
            <span className="hinweis">
              {inventur.anzahl_gezaehlt} von {inventur.anzahl_materialien} Materialien gezählt
            </span>
          )}
          <button className={ansicht === 'zaehlung' ? 'haupt' : ''} onClick={() => setAnsicht('zaehlung')}>
            Zählung und Abgleich
          </button>
          <button className={ansicht === 'jahr' ? 'haupt' : ''} onClick={() => setAnsicht('jahr')}>
            Jahresübersicht
          </button>
          <a className="knopf" href={api.exportBestandsliste(inventur.id, gruppe || undefined)} download>
            Als Excel (Bestandsliste)
          </a>
          {bearbeitbar && (
            <>
              <button
                disabled={arbeitet}
                onClick={() => void ausfuehren(async () => {
                  if (gewaehlt !== null) await api.sollAktualisieren(gewaehlt)
                  blattLaden()
                }, 'Soll aus den eingelesenen Reports aktualisiert.')}
              >
                Soll aus SelectLine holen
              </button>
              <button className="haupt" disabled={arbeitet || inventur.anzahl_gezaehlt === 0} onClick={abschliessen}>
                Abschließen und zur Freigabe
              </button>
            </>
          )}
          {inventur.import_lauf_id && inventur.status !== 'offen' && (
            <button onClick={() => navigate(`/importe/${inventur.import_lauf_id}`)}>
              Import {inventur.import_lauf_id} ansehen
            </button>
          )}
        </div>
      )}

      {!blatt && laedt && <p className="hinweis">Inventur wird geladen…</p>}
      {!inventur && !laedt && inventuren.length === 0 && (
        <p className="leer">Noch keine Inventur. Stichtag wählen und anlegen - der letzte Monatsletzte ist vorgeschlagen.</p>
      )}

      {inventur && ansicht === 'zaehlung' && (
        <>
          {bearbeitbar && (
            <div className="karte">
              <h3>Zählzettel eintippen</h3>
              <p className="hinweis">
                Frei, so wie es auf dem Zettel steht - „Sanna Wickler: 14 Rollen, zwei angefangene mit 10,3 cm,
                Sperrlager 100.683 Stück". Die KI ordnet den Zähleinheiten zu, gerechnet wird mit den Faktoren
                aus den Stammdaten. Übernommen wird erst nach deinem Klick.
              </p>
              <textarea value={zettel} onChange={(e) => setZettel(e.target.value)} style={{ minHeight: 110 }} />
              <div className="leiste" style={{ marginTop: 8 }}>
                <button className="haupt" disabled={arbeitet || !zettel.trim()} onClick={zettelZuordnen}>
                  Zuordnen
                </button>
                {arbeitet && <span className="hinweis">Wird verarbeitet.</span>}
              </div>
              {zettelAntwort?.rueckfragen.length ? (
                <Meldung art="warnung" titel="Zu klären">
                  <ul>
                    {zettelAntwort.rueckfragen.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </Meldung>
              ) : null}
              {zettelAntwort?.zaehlungen.map((z) => (
                <div key={z.artikelnummer} className="leiste" style={{ borderTop: '1px solid var(--linie)', paddingTop: 8 }}>
                  <b>
                    {z.artikelnummer} {z.bezeichnung}
                  </b>
                  <span>{z.positionen.map((p) => `${menge(p.anzahl)} × ${p.bezeichnung}`).join(' + ')}</span>
                  <span>
                    = <b>{menge(z.gezaehlt)}</b>
                    {z.gesperrt ? ` (davon ${menge(z.gesperrt)} Sperrlager)` : ''}
                  </span>
                  {z.sicherheit === 'unsicher' && <span className="status gesperrt">unsicher</span>}
                  {z.begruendung && <span className="hinweis">{z.begruendung}</span>}
                  <button disabled={arbeitet} onClick={() => vorschlagUebernehmen(z.artikelnummer, z.positionen)}>
                    Übernehmen
                  </button>
                </div>
              ))}
            </div>
          )}

          {blatt && blatt.zeilen.length > 0 && (
            <div className="zaehl-leiste">
              {blatt.zeilen.map((zeile) => (
                <button
                  key={zeile.artikelnummer}
                  className={zeile.gezaehlt !== null ? 'gezaehlt' : 'offen'}
                  title={`${zeile.bezeichnung}: ${zeile.gezaehlt !== null ? `gezählt ${menge(zeile.gezaehlt)}` : 'noch nicht gezählt'}`}
                  onClick={() => springen(zeile)}
                >
                  {zeile.artikelnummer} {zeile.gezaehlt !== null ? 'gezählt' : 'offen'}
                </button>
              ))}
              <span className="hinweis" style={{ alignSelf: 'center' }}>
                Ein Klick springt zum Block. Gezählte Blöcke sind eingeklappt; ein Klick öffnet sie.
              </span>
            </div>
          )}

          {blatt && blatt.zeilen.length > 0 && blatt.zeilen.every((z) => !z.soll_quelle) && (
            <p className="hinweis">
              Soll aus SelectLine liegt noch nicht vor: Bis der Bestandsreport zum Stichtag eingelesen ist („Soll aus
              SelectLine holen“ oben), zeigt die Zählung keine Differenz.
            </p>
          )}
          {blatt?.zeilen.map((zeile) => {
            if (istEingeklappt(zeile)) {
              return (
                <div
                  key={zeile.artikelnummer}
                  id={`zaehlung-${zeile.artikelnummer}`}
                  className={`karte eingeklappt ${hervorgehoben === zeile.artikelnummer ? 'angesprungen' : ''}`}
                >
                  <div className="leiste">
                    <b onClick={() => aufklappen(zeile.artikelnummer, false)} title="Aufklappen">
                      {zeile.artikelnummer} {zeile.bezeichnung}
                    </b>
                    <span className="status gebucht">gezählt {menge(zeile.gezaehlt)} {zeile.einheit}</span>
                    {zeile.differenz !== null && (
                      <span className={`hinweis ${Math.abs(zeile.differenz) > 0.5 ? 'unter-mindestbestand' : ''}`}>
                        Differenz {differenz(zeile.differenz)} {zeile.einheit}
                      </span>
                    )}
                    {zeile.vermerk && <span className="hinweis">{zeile.vermerk}</span>}
                    <button onClick={() => aufklappen(zeile.artikelnummer, false)}>Aufklappen</button>
                  </div>
                </div>
              )
            }
            const werte = eingaben[zeile.artikelnummer] ?? []
            // Bei fehlerhafter Eingabe steht in der Summenzeile der Fehler, keine Zahl - eine negative oder
            // aus „abc" als 0 gerechnete Summe sähe wie ein Ergebnis aus.
            const eingabefehler = zaehlfehler(zeile, werte)
            const summe = zeile.zaehleinheiten.reduce((s, e, i) => s + (zahlLesen(werte[i] ?? '') ?? 0) * (e.faktor ?? 1), 0)
            const sperr = zeile.zaehleinheiten.reduce(
              (s, e, i) => s + (e.sperrlager ? (zahlLesen(werte[i] ?? '') ?? 0) * (e.faktor ?? 1) : 0),
              0,
            )
            const diffKlasse = zeile.differenz !== null && Math.abs(zeile.differenz) > 0.5 ? 'unter-mindestbestand' : ''
            return (
              <div
                key={zeile.artikelnummer}
                id={`zaehlung-${zeile.artikelnummer}`}
                className={`karte ${hervorgehoben === zeile.artikelnummer ? 'angesprungen' : ''}`}
              >
                <div className="leiste" style={{ marginBottom: 6 }}>
                  <b>
                    {zeile.artikelnummer} {zeile.bezeichnung}
                  </b>
                  <span className="hinweis">{zeile.einheit}{zeile.mvn ? ` · MVN ${menge(zeile.mvn)}` : ' · keine MVN'}</span>
                  {zeile.hinweis && <span className="status">{zeile.hinweis}</span>}
                  {zeile.gezaehlt !== null && (
                    <button style={{ marginLeft: 'auto' }} onClick={() => aufklappen(zeile.artikelnummer, false)}>
                      Einklappen
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16 }}>
                  <table style={{ width: '100%', minWidth: 0 }}>
                    <thead>
                      <tr>
                        <th className="h1">Zähleinheit</th>
                        <th className="h1 zahl">Faktor</th>
                        <th className="h1 zahl">Anzahl</th>
                        <th className="h1 zahl">{zeile.einheit}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zeile.zaehleinheiten.map((einheit, i) => (
                        <tr key={i}>
                          <td>{einheit.bezeichnung}</td>
                          <td className="zahl hinweis">{einheit.faktor !== null ? menge(einheit.faktor) : '1 (direkt)'}</td>
                          <td className="zahl">
                            <input
                              type="text"
                              style={{ width: 90, textAlign: 'right' }}
                              value={werte[i] ?? ''}
                              disabled={!bearbeitbar}
                              onChange={(e) => {
                                // Eine Backend-Ablehnung gilt für den Stand, der geschickt wurde - beim Tippen weg damit.
                                if (blockFehler[zeile.artikelnummer]) blockFehlerSetzen(zeile.artikelnummer, null)
                                setEingaben((alt) => {
                                  const kopie = [...(alt[zeile.artikelnummer] ?? [])]
                                  kopie[i] = e.target.value
                                  return { ...alt, [zeile.artikelnummer]: kopie }
                                })
                              }}
                            />
                          </td>
                          <td className="zahl">
                            {werte[i]
                              ? zahlLesen(werte[i]) === null
                                ? 'keine Anzahl'
                                : menge((zahlLesen(werte[i]) ?? 0) * (einheit.faktor ?? 1))
                              : ''}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="gruppe-summe">Gesamt</td>
                        <td />
                        <td className="zahl hinweis">{!eingabefehler && sperr ? `davon Sperrlager ${menge(sperr)}` : ''}</td>
                        <td className="zahl gruppe-summe">{eingabefehler ? 'Eingabe prüfen' : menge(summe)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <table style={{ width: '100%', minWidth: 0 }}>
                    <tbody>
                      <tr>
                        <td>Soll (SelectLine)</td>
                        <td className="zahl">{zeile.soll !== null ? menge(zeile.soll) : '–'}</td>
                        <td className="zahl">{zeile.soll_t !== null ? `${tonnen(zeile.soll_t)} t` : ''}</td>
                      </tr>
                      <tr>
                        <td>Gezählt (gespeichert)</td>
                        <td className="zahl">{zeile.gezaehlt !== null ? menge(zeile.gezaehlt) : '–'}</td>
                        <td className="zahl">{zeile.gezaehlt_t !== null ? `${tonnen(zeile.gezaehlt_t)} t` : ''}</td>
                      </tr>
                      {zeile.ist_selectline != null && (
                        <tr>
                          <td>Ist laut SelectLine{zeile.gezaehlt === null ? ' (gilt)' : ''}</td>
                          <td className="zahl">{menge(zeile.ist_selectline)}</td>
                          <td className="zahl">{zeile.ist_selectline_t != null ? `${tonnen(zeile.ist_selectline_t)} t` : ''}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="hinweis">davon Sperrlager / frei</td>
                        <td className="zahl hinweis">{zeile.gezaehlt !== null ? `${menge(zeile.gesperrt)} / ${menge(zeile.frei)}` : ''}</td>
                        <td />
                      </tr>
                      <tr>
                        <td className="gruppe-summe">Differenz{zeile.ist_quelle ? ` (${zeile.ist_quelle})` : ''}</td>
                        <td className={`zahl gruppe-summe ${diffKlasse}`}>{zeile.differenz !== null ? differenz(zeile.differenz) : '–'}</td>
                        <td className={`zahl gruppe-summe ${diffKlasse}`}>
                          {zeile.differenz_t !== null ? `${differenz(zeile.differenz_t)} t` : ''}
                          {zeile.differenz_wert !== null ? ` · ${differenz(zeile.differenz_wert)} EUR` : ''}
                        </td>
                      </tr>
                      {(zeile.soll_quelle || zeile.wert_je_einheit !== null) && (
                        <tr>
                          <td className="hinweis" colSpan={3}>
                            {zeile.soll_quelle ?? ''}
                            {zeile.wert_je_einheit !== null ? ` · ${preis(zeile.wert_je_einheit, 5)} EUR je ${zeile.einheit}` : ''}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {(eingabefehler || blockFehler[zeile.artikelnummer]) && (
                  <Meldung art="fehler" titel="Nicht gespeichert">
                    {eingabefehler ?? blockFehler[zeile.artikelnummer]}
                  </Meldung>
                )}
                <div className="leiste" style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    style={{ width: 320, maxWidth: '100%' }}
                    placeholder="Vermerk (Nachzählen, Reklamation …)"
                    value={vermerke[zeile.artikelnummer] ?? ''}
                    disabled={!bearbeitbar}
                    onChange={(e) => setVermerke((alt) => ({ ...alt, [zeile.artikelnummer]: e.target.value }))}
                  />
                  <button className="haupt" disabled={arbeitet || !bearbeitbar} onClick={() => speichern(zeile)}>
                    Zählung speichern
                  </button>
                </div>
              </div>
            )
          })}
          {blatt && blatt.zeilen.length === 0 && <p className="leer">Keine Materialien in dieser Gruppe.</p>}
        </>
      )}

      {inventur && ansicht === 'jahr' && (
        <>
          <div className="leiste">
            <label>
              Jahr{' '}
              <select value={jahr} onChange={(e) => setJahr(Number(e.target.value))}>
                {[jahr - 1, jahr, jahr + 1].map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </select>
            </label>
            <a className="knopf" href={api.exportAuswertung(jahr, gruppe || undefined)} download>
              Als Excel (Auswertung)
            </a>
            <span className="hinweis">Je Monat Soll und Ist in Tonnen, Differenz in Tonnen und Euro. Wie die Datei INVENTUR - AUSWERTUNG.</span>
          </div>
          <div className="tabellenrahmen">
            <table>
              <thead>
                <tr>
                  <th className="tag-spalte h1" rowSpan={2}>
                    Artikel
                  </th>
                  {monateMitDaten.map((m) => (
                    <th key={m} className="h1 block" colSpan={4}>
                      {MONATE_KURZ[m - 1]} {jahr}
                    </th>
                  ))}
                  <th className="h1 block" colSpan={2}>
                    Jahr
                  </th>
                </tr>
                <tr>
                  {monateMitDaten.map((m) => (
                    <Fragment key={m}>
                      <th className="h2 zahl">Soll t</th>
                      <th className="h2 zahl">Ist t</th>
                      <th className="h2 zahl">Diff t</th>
                      <th className="h2 zahl">Diff EUR</th>
                    </Fragment>
                  ))}
                  <th className="h2 zahl">Diff t</th>
                  <th className="h2 zahl">Diff EUR</th>
                </tr>
              </thead>
              <tbody>
                {jahresZeilen.map((z) => (
                  <tr key={z.artikelnummer}>
                    <td className="tag-spalte">
                      {z.artikelnummer} {z.bezeichnung}
                    </td>
                    {monateMitDaten.map((m) => {
                      const monat = z.monate[String(m)]
                      return (
                        <Fragment key={m}>
                          <td className="zahl">{monat ? tonnen(monat.soll_t) : ''}</td>
                          <td className="zahl">{monat ? tonnen(monat.ist_t) : ''}</td>
                          <td className="zahl">{monat?.differenz_t != null ? differenz(monat.differenz_t) : ''}</td>
                          <td className="zahl">{monat?.differenz_wert != null ? differenz(monat.differenz_wert) : ''}</td>
                        </Fragment>
                      )
                    })}
                    <td className="zahl gruppe-summe">{differenz(z.summe_differenz_t)}</td>
                    <td className="zahl gruppe-summe">{differenz(z.summe_differenz_wert)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {jahresZeilen.length === 0 && jahresDaten !== null && (
              <p className="leer">
                {unfertigeImJahr
                  ? `Noch keine abgeschlossene Inventur in ${jahr} - die ${unfertigeImJahr.status === 'offen' ? 'offene' : 'unfertige'} vom ${datum(unfertigeImJahr.stichtag)} zählt nach der Freigabe.`
                  : `Noch keine Inventur in ${jahr}.`}
              </p>
            )}
          </div>
        </>
      )}
    </>
  )
}
